import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { HiPhone, HiMail, HiGlobeAlt } from 'react-icons/hi';
import { clsx } from 'clsx';
import { PHP_MAIL_URL } from '../config/phpmail';
import './Contact.css';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

/** Bundles subject + body; tagged so the owner can see it came from AEuromotive */
function buildMailMessage(subject: string, body: string): string {
  return `[AEuromotive contact form]\n\nSubject: ${subject}\n\n${body}`;
}

const Contact: React.FC = () => {
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [serverError, setServerError] = useState<string>('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<ContactFormData>();

  const onSubmit: SubmitHandler<ContactFormData> = async (data) => {
    setServerError('');
    setSubmitStatus('idle');
    try {
      const response = await fetch(PHP_MAIL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name.trim(),
          email: data.email.trim(),
          phone: data.phone.trim(),
          message: buildMailMessage(data.subject.trim(), data.message.trim()),
          member_id: '',
          address: '',
          city: '',
          state: '',
          zip: ''
        })
      });

      const raw = await response.text();
      let result: { success?: boolean; error?: string };
      try {
        result = JSON.parse(raw) as { success?: boolean; error?: string };
      } catch {
        setSubmitStatus('error');
        const host = typeof window !== 'undefined' ? window.location.hostname : '';
        const isLocalDev = host === 'localhost' || host === '127.0.0.1';
        setServerError(
          isLocalDev
            ? 'The mail server did not return JSON. With npm run dev, requests are proxied to the host in vite.config (default https://www.aeuromotive.com); set VITE_DEV_PHP_MAIL_PROXY if your live site uses a different origin.'
            : 'The server did not return a valid response (often a missing PHPMailer install or a PHP error). Confirm phpmail.php and ~/PHPMailer are deployed for this DreamHost user, or contact us by email.'
        );
        return;
      }

      if (!response.ok) {
        setSubmitStatus('error');
        setServerError(result.error || `Server error (${response.status}).`);
        return;
      }

      if (result.success) {
        setSubmitStatus('success');
        reset();
      } else {
        setSubmitStatus('error');
        setServerError(result.error || 'The server could not send your message.');
      }
    } catch (e) {
      setSubmitStatus('error');
      setServerError(
        e instanceof TypeError
          ? 'Network error. Check your connection or dev proxy (VITE_DEV_PHP_MAIL_PROXY).'
          : 'Something went wrong. Please try again.'
      );
    }
  };

  return (
    <div className="contact-page">
      <div className="content-card">
        <h1>Contact Us</h1>
        <p>
          Have questions about our training programs or technical support services?
          We&apos;re here to help! Contact us using the form below or call us directly.
        </p>

        <div className="contact-info">
          <h3>Get in Touch</h3>
          <div className="contact-methods">
            <div className="contact-method">
              <HiPhone className="contact-icon" />
              <span>
                <strong>Phone:</strong> 800-660-7156
              </span>
            </div>
            <div className="contact-method">
              <HiMail className="contact-icon" />
              <span>
                <strong>Email:</strong> info@aeuromotive.com
              </span>
            </div>
            <div className="contact-method">
              <HiGlobeAlt className="contact-icon" />
              <span>
                <strong>Technical Forum:</strong>{' '}
                <a href="http://forum.aeuromotive.com/" target="_blank" rel="noopener noreferrer">
                  forum.aeuromotive.com
                </a>
              </span>
            </div>
          </div>
        </div>

        <div className="contact-form">
          <h3>Send us a Message</h3>
          {submitStatus === 'success' && (
            <div className="alert alert-success">
              Thank you for your message. We will get back to you soon!
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="alert alert-error">
              {serverError || 'There was an error sending your message. Please try again.'}
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                className={clsx('form-input', errors.name && 'error')}
                {...register('name', { required: 'Name is required' })}
              />
              {errors.name && <span className="error-message">{errors.name.message}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                className={clsx('form-input', errors.email && 'error')}
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Invalid email address'
                  }
                })}
              />
              {errors.email && <span className="error-message">{errors.email.message}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone *</label>
              <input
                type="tel"
                id="phone"
                className={clsx('form-input', errors.phone && 'error')}
                {...register('phone', {
                  required: 'Phone is required',
                  pattern: {
                    value: /^[\d\s\-+()]+$/,
                    message: 'Enter a valid phone number'
                  }
                })}
              />
              {errors.phone && <span className="error-message">{errors.phone.message}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject *</label>
              <input
                type="text"
                id="subject"
                className={clsx('form-input', errors.subject && 'error')}
                {...register('subject', { required: 'Subject is required' })}
              />
              {errors.subject && <span className="error-message">{errors.subject.message}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                rows={4}
                className={clsx('form-input', errors.message && 'error')}
                {...register('message', { required: 'Message is required' })}
              />
              {errors.message && <span className="error-message">{errors.message.message}</span>}
            </div>

            <button
              type="submit"
              className={clsx('submit-btn', isSubmitting && 'loading')}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
